import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllInvoicesQueryKey } from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, User, Receipt } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/admin/invoices/correct/$id")({
	component: InvoiceCorrectionPage,
});

interface EditRow {
	attendanceId?: string;
	studentId: string;
	studentName: string;
	courseId: string;
	courseSubject: string;
	courseLevel: string;
	courseTimeSlot: string;
	pricePerHour: number;
	date: string;
	status: string;
	hourlyAttendance: Record<string, boolean>;
	overtimeHours: number;
	hourSlots: string[];
	notes?: string;
}

interface EditData {
	invoiceId: string;
	invoiceNumber: string;
	suggestedInvoiceNumber: string;
	parentId: string;
	parentFirstName: string;
	parentLastName: string;
	billingPeriodStart: string;
	billingPeriodEnd: string;
	rows: EditRow[];
}

function InvoiceCorrectionPage() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [rows, setRows] = useState<EditRow[]>([]);
	const [newInvoiceNumber, setNewInvoiceNumber] = useState<string>("");

	const { data, isLoading } = useQuery<EditData>({
		queryKey: ["invoice", "attendances-edit", id],
		queryFn: async () => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/invoices/${id}/attendances`,
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				},
			);
			if (!response.ok) throw new Error("Erreur lors du chargement des présences");
			return response.json();
		},
	});

	useEffect(() => {
		if (data) {
			setRows(data.rows.map((r) => ({ ...r, hourlyAttendance: { ...r.hourlyAttendance } })));
			setNewInvoiceNumber(data.suggestedInvoiceNumber || "");
		}
	}, [data]);

	const regenerate = useMutation({
		mutationFn: async () => {
			const attendances = rows
				.map((row) => {
					const hourlyAttendance: Record<string, boolean> = {};
					for (const slot of row.hourSlots) {
						hourlyAttendance[slot] = !!row.hourlyAttendance[slot];
					}
					const hoursPresent = Object.values(hourlyAttendance).filter(Boolean).length;
					const status = hoursPresent > 0 ? "PRESENT" : "ABSENT";
					return {
						attendanceId: row.attendanceId,
						studentId: row.studentId,
						courseId: row.courseId,
						date: row.date,
						status,
						hourlyAttendance,
						overtimeHours: row.overtimeHours || 0,
						notes: row.notes,
						_hasData: !!row.attendanceId || hoursPresent > 0 || (row.overtimeHours || 0) > 0,
					};
				})
				// On n'envoie que les présences existantes ou renseignées pour éviter de
				// créer des enregistrements vides pour chaque date attendue.
				.filter((a) => a._hasData)
				.map(({ _hasData, attendanceId, ...rest }) => rest);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/invoices/${id}/regenerate`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify({ newInvoiceNumber, attendances }),
				},
			);
			if (!response.ok) {
				const message = await response.text().catch(() => "");
				throw new Error(message || "Erreur lors de la réédition");
			}
			return response.json();
		},
		onSuccess: (newInvoice: any) => {
			toast.success("Nouvelle facture générée et envoyée au parent");
			queryClient.invalidateQueries({ queryKey: getAllInvoicesQueryKey() });
			navigate({ to: "/admin/invoices/$id", params: { id: newInvoice.id } });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Erreur lors de la réédition de la facture");
		},
	});

	const toggleSlot = (rowIndex: number, slot: string) => {
		setRows((prev) => {
			const next = [...prev];
			const row = { ...next[rowIndex] };
			row.hourlyAttendance = {
				...row.hourlyAttendance,
				[slot]: !row.hourlyAttendance[slot],
			};
			next[rowIndex] = row;
			return next;
		});
	};

	const setOvertime = (rowIndex: number, value: string) => {
		const parsed = parseInt(value, 10);
		setRows((prev) => {
			const next = [...prev];
			next[rowIndex] = {
				...next[rowIndex],
				overtimeHours: isNaN(parsed) || parsed < 0 ? 0 : parsed,
			};
			return next;
		});
	};

	// Groupe les lignes par élève + cours
	const groups = useMemo(() => {
		const map = new Map<
			string,
			{
				studentName: string;
				courseSubject: string;
				courseLevel: string;
				courseTimeSlot: string;
				hourSlots: string[];
				items: { row: EditRow; index: number }[];
			}
		>();
		rows.forEach((row, index) => {
			const key = `${row.studentId}_${row.courseId}`;
			if (!map.has(key)) {
				map.set(key, {
					studentName: row.studentName,
					courseSubject: row.courseSubject,
					courseLevel: row.courseLevel,
					courseTimeSlot: row.courseTimeSlot,
					hourSlots: row.hourSlots,
					items: [],
				});
			}
			map.get(key)!.items.push({ row, index });
		});
		return Array.from(map.values());
	}, [rows]);

	const estimatedTotal = useMemo(() => {
		return rows.reduce((acc, row) => {
			const hoursPresent = row.hourSlots.filter((s) => row.hourlyAttendance[s]).length;
			const billable = hoursPresent > 0 ? hoursPresent + (row.overtimeHours || 0) : 0;
			return acc + billable * (row.pricePerHour || 0);
		}, 0);
	}, [rows]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="p-6 text-center">
				<h2 className="text-2xl font-bold mb-4">Facture non trouvée</h2>
				<Button variant="outline" onClick={() => navigate({ to: "/admin/invoices" })}>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Retour à la liste
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate({ to: "/admin/invoices/$id", params: { id } })}
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Retour
				</Button>
			</div>

			<div>
				<h1 className="text-3xl font-bold">Corriger la facture #{data.invoiceNumber}</h1>
				<p className="text-gray-600 mt-2">
					Corrigez les présences puis générez une nouvelle facture. La facture
					d'origine sera annulée et une notification (email + SMS) sera envoyée au parent.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<User className="h-5 w-5" />
						{data.parentFirstName} {data.parentLastName}
					</CardTitle>
					<CardDescription>
						Période du{" "}
						{format(new Date(data.billingPeriodStart), "dd/MM/yyyy", { locale: fr })} au{" "}
						{format(new Date(data.billingPeriodEnd), "dd/MM/yyyy", { locale: fr })}
					</CardDescription>
				</CardHeader>
			</Card>

			{groups.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center text-gray-500">
						Aucune séance à corriger pour cette période.
					</CardContent>
				</Card>
			) : (
				groups.map((group, gIdx) => (
					<Card key={gIdx}>
						<CardHeader>
							<CardTitle className="text-lg">{group.studentName}</CardTitle>
							<CardDescription>
								{group.courseSubject} - {group.courseLevel} ({group.courseTimeSlot})
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="overflow-x-auto">
								<table className="w-full border-collapse text-sm">
									<thead>
										<tr className="border-b bg-gray-50">
											<th className="p-2 text-left">Date</th>
											{group.hourSlots.map((slot) => (
												<th key={slot} className="p-2 text-center text-xs">
													{slot}
												</th>
											))}
											<th className="p-2 text-center text-xs">Heures sup.</th>
											<th className="p-2 text-center text-xs">Total</th>
										</tr>
									</thead>
									<tbody>
										{group.items.map(({ row, index }) => {
											const hoursPresent = group.hourSlots.filter(
												(s) => row.hourlyAttendance[s],
											).length;
											const billable =
												hoursPresent > 0 ? hoursPresent + (row.overtimeHours || 0) : 0;
											return (
												<tr key={index} className="border-b">
													<td className="p-2 font-medium whitespace-nowrap">
														{format(new Date(row.date), "EEEE dd/MM/yyyy", {
															locale: fr,
														})}
													</td>
													{group.hourSlots.map((slot) => {
														const present = !!row.hourlyAttendance[slot];
														return (
															<td key={slot} className="p-2 text-center">
																<button
																	type="button"
																	onClick={() => toggleSlot(index, slot)}
																	className={
																		present
																			? "w-7 h-7 rounded bg-green-500 text-white flex items-center justify-center text-xs font-bold mx-auto"
																			: "w-7 h-7 rounded border border-gray-300 hover:bg-gray-100 mx-auto"
																	}
																	title={present ? "Présent - cliquer pour effacer" : "Absent - cliquer pour marquer présent"}
																>
																	{present ? "X" : ""}
																</button>
															</td>
														);
													})}
													<td className="p-2 text-center">
														<Input
															type="number"
															min="0"
															value={row.overtimeHours || 0}
															onChange={(e) => setOvertime(index, e.target.value)}
															className="w-16 text-center text-sm mx-auto"
														/>
													</td>
													<td className="p-2 text-center whitespace-nowrap">
														{(billable * (row.pricePerHour || 0)).toFixed(2)} €
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>
				))
			)}

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Receipt className="h-5 w-5" />
						Réédition de la facture
					</CardTitle>
					<CardDescription>
						Indiquez le numéro de la nouvelle facture avant de la générer.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex items-center justify-between">
						<span className="text-sm text-gray-600">Nouveau montant total estimé</span>
						<span className="text-2xl font-bold">{estimatedTotal.toFixed(2)} €</span>
					</div>
					<div>
						<Label>Numéro de la nouvelle facture</Label>
						<Input
							value={newInvoiceNumber}
							onChange={(e) => setNewInvoiceNumber(e.target.value)}
							placeholder="Ex: INV-000123-R"
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button
							variant="outline"
							onClick={() => navigate({ to: "/admin/invoices/$id", params: { id } })}
						>
							Annuler
						</Button>
						<Button
							onClick={() => {
								if (!newInvoiceNumber.trim()) {
									toast.error("Veuillez indiquer le numéro de la nouvelle facture");
									return;
								}
								if (
									confirm(
										"Confirmer la réédition ? La facture d'origine sera annulée et une nouvelle facture sera envoyée au parent.",
									)
								) {
									regenerate.mutate();
								}
							}}
							disabled={regenerate.isPending}
						>
							<Save className="mr-2 h-4 w-4" />
							{regenerate.isPending ? "Génération..." : "Générer la nouvelle facture"}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
